import { onScopeDispose, ref } from 'vue';
import type { Ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { v4 as uuidv4 } from 'uuid';
import { isBusinessType } from '@/domain/store-setup/business-types';
import type { IDayHours, TWeekHours } from '@/domain/store-setup/opening-hours';
import {
  countryFromTimeZone,
  currencyForCountry,
} from '@/domain/store-setup/store-country';
import type { TBusinessType } from '@/interfaces/IStoreSetup';
import { Globals } from '@/modules/Globals';

/** How the pin of the location was obtained (D6: a country-centre pin is never saved). */
export type TSetupPinSource = 'place' | 'geocode' | 'geolocation' | 'manual';
export type TStoreSetupDraftStep = 'location' | 'business';

export interface IStoreSetupDraftLocation {
  placeId: string | null;
  placeName: string | null;
  address: string;
  reference: string;
  lat: number;
  lng: number;
  province: string | null;
  city: string | null;
  pinSource: TSetupPinSource;
  primaryType: string | null;
  types: string[];
}

/** The `/new` flow before the store exists (plan §4.12), kept in this device's storage. */
export interface IStoreSetupDraft {
  v: 1;
  step: TStoreSetupDraftStep;
  partnerAccepted: boolean;
  country: string;
  location: IStoreSetupDraftLocation | null;
  name: string;
  slug: string;
  slugEdited: boolean;
  businessType: TBusinessType;
  businessTypeFromGoogle: boolean;
  hours: TWeekHours | null;
  hoursFromGoogle: boolean;
  hoursConfirmed: boolean;
  currency: string | null;
  logoPath: string | null;
  /** The uploaded logo's dominant colour («#RRGGBB», CON-6): the preview's brand colour (D-ADM-5). */
  logoColor: string | null;
  /** UUID v4, one per draft: a create retried after a lost 201 returns the same store. */
  idempotencyKey: string;
  /** Epoch ms of the last change. */
  savedAt: number;
}

/** What the screens may change; the version, the idempotency key and the timestamp are owned here. */
export type TStoreSetupDraftPatch = Partial<
  Omit<IStoreSetupDraft, 'v' | 'idempotencyKey' | 'savedAt'>
>;

export interface IUseStoreSetupDraft {
  draft: Ref<IStoreSetupDraft>;
  /** A valid stored draft was found when the composable started. */
  restored: boolean;
  patch(patch: TStoreSetupDraftPatch): void;
  /** «Empezar de nuevo»: a fresh draft (new idempotency key); the stored one is removed. */
  reset(): void;
  /** After the store was created: the stored draft is removed and nothing is written again. */
  clear(): void;
}

export const STORE_SETUP_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const STORE_SETUP_DRAFT_WRITE_MS = 300;

const DEFAULT_COUNTRY = 'AR';
const PIN_SOURCES: readonly TSetupPinSource[] = [
  'place',
  'geocode',
  'geolocation',
  'manual',
];
const DRAFT_STEPS: readonly TStoreSetupDraftStep[] = ['location', 'business'];
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isStringList(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((entry) => typeof entry === 'string')
  );
}

function isPinSource(value: unknown): value is TSetupPinSource {
  return PIN_SOURCES.some((source) => source === value);
}

function isDraftStep(value: unknown): value is TStoreSetupDraftStep {
  return DRAFT_STEPS.some((step) => step === value);
}

function isLocation(value: unknown): value is IStoreSetupDraftLocation {
  return (
    isRecord(value) &&
    isNullableString(value.placeId) &&
    isNullableString(value.placeName) &&
    typeof value.address === 'string' &&
    typeof value.reference === 'string' &&
    isFiniteNumber(value.lat) &&
    isFiniteNumber(value.lng) &&
    isNullableString(value.province) &&
    isNullableString(value.city) &&
    isPinSource(value.pinSource) &&
    isNullableString(value.primaryType) &&
    isStringList(value.types)
  );
}

function isDayHours(value: unknown): value is IDayHours {
  return (
    isRecord(value) &&
    typeof value.open === 'boolean' &&
    Array.isArray(value.ranges) &&
    value.ranges.every(
      (range) =>
        isRecord(range) &&
        typeof range.from === 'string' &&
        typeof range.to === 'string'
    )
  );
}

function isWeekHours(value: unknown): value is TWeekHours {
  return Array.isArray(value) && value.length === 7 && value.every(isDayHours);
}

/** The draft shape, field by field (anything else in storage is ignored). */
function isStoreSetupDraft(value: unknown): value is IStoreSetupDraft {
  return (
    isRecord(value) &&
    value.v === 1 &&
    isDraftStep(value.step) &&
    typeof value.partnerAccepted === 'boolean' &&
    typeof value.country === 'string' &&
    value.country.length > 0 &&
    (value.location === null || isLocation(value.location)) &&
    typeof value.name === 'string' &&
    typeof value.slug === 'string' &&
    typeof value.slugEdited === 'boolean' &&
    isBusinessType(value.businessType) &&
    typeof value.businessTypeFromGoogle === 'boolean' &&
    (value.hours === null || isWeekHours(value.hours)) &&
    typeof value.hoursFromGoogle === 'boolean' &&
    typeof value.hoursConfirmed === 'boolean' &&
    isNullableString(value.currency) &&
    isNullableString(value.logoPath) &&
    isNullableString(value.logoColor) &&
    typeof value.idempotencyKey === 'string' &&
    UUID_V4.test(value.idempotencyKey) &&
    isFiniteNumber(value.savedAt)
  );
}

/** A stored draft when it is well formed and younger than 7 days; null otherwise. */
export function parseStoredDraft(
  raw: string | null,
  now: number
): IStoreSetupDraft | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  // Drafts stored before the logo colour existed keep restoring: rejecting
  // them would throw away the owner's half-done setup.
  if (isRecord(value) && !('logoColor' in value))
    value = { ...value, logoColor: null };
  if (!isStoreSetupDraft(value)) return null;
  return now - value.savedAt > STORE_SETUP_DRAFT_TTL_MS ? null : value;
}

function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

/** A new draft: the country of the device time zone (else AR) and its currency. */
export function freshStoreSetupDraft(
  now: number,
  timeZone: string | null = deviceTimeZone()
): IStoreSetupDraft {
  const country = countryFromTimeZone(timeZone) ?? DEFAULT_COUNTRY;
  return {
    v: 1,
    step: 'location',
    partnerAccepted: false,
    country,
    location: null,
    name: '',
    slug: '',
    slugEdited: false,
    businessType: 'restaurant',
    businessTypeFromGoogle: false,
    hours: null,
    hoursFromGoogle: false,
    hoursConfirmed: false,
    currency: currencyForCountry(country),
    logoPath: null,
    logoColor: null,
    idempotencyKey: uuidv4(),
    savedAt: now,
  };
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage full, blocked or missing: the flow keeps working without it.
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // See writeStorage.
  }
}

/**
 * The local draft of `/new` (plan §4.12, §10.4): restored when a valid one
 * younger than 7 days exists, written 300 ms after every change, every
 * storage access guarded (missing or throwing storage = start fresh).
 */
export function useStoreSetupDraft(userId: number): IUseStoreSetupDraft {
  const key = Globals.setupDraftKey(userId);
  const stored = parseStoredDraft(readStorage(key), Date.now());
  const draft = ref<IStoreSetupDraft>(
    stored ?? freshStoreSetupDraft(Date.now())
  );
  /** A change is waiting for the debounced write. */
  let dirty = false;

  function flush(): void {
    if (!dirty) return;
    dirty = false;
    writeStorage(key, JSON.stringify(draft.value));
  }
  const scheduleWrite = useDebounceFn(flush, STORE_SETUP_DRAFT_WRITE_MS);

  function patch(changes: TStoreSetupDraftPatch): void {
    draft.value = { ...draft.value, ...changes, savedAt: Date.now() };
    dirty = true;
    void scheduleWrite();
  }

  function clear(): void {
    dirty = false;
    removeStorage(key);
  }

  function reset(): void {
    clear();
    draft.value = freshStoreSetupDraft(Date.now());
  }

  // Leaving the page within the debounce window must not lose the last change.
  onScopeDispose(flush);

  return { draft, restored: stored !== null, patch, reset, clear };
}
